import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useApp } from '@/app/AppContext'
import { can } from '@/auth/permissions'
import { ApiError } from '@/lib/api'
import {
  downloadAttachment,
  fetchAttachments,
  uploadAttachment,
  type AttachmentView,
} from './attachmentApi'

export interface AttachmentsPanelProps {
  entityType: string
  entityId: string
  resource: string
}

/** Real attachment list: upload, antivirus state and permission-checked download (#86). */
export function AttachmentsPanel({ entityType, entityId, resource }: AttachmentsPanelProps) {
  const { t, user } = useApp()
  const queryClient = useQueryClient()
  const [failure, setFailure] = useState<string | null>(null)

  const queryKey = ['attachments', entityType, entityId] as const
  const query = useQuery({
    queryKey,
    queryFn: ({ signal }) => fetchAttachments(entityType, entityId, signal),
    // A fresh upload is pending. Poll only while there is unfinished work so the button
    // appears after ClamAV's verdict without turning every open card into a permanent poll.
    refetchInterval: (current) =>
      current.state.data?.some((item) => item.scanStatus === 'pending') ? 2000 : false,
  })

  const upload = useMutation({
    mutationFn: (file: File) => uploadAttachment(entityType, entityId, file),
    onSuccess: () => {
      setFailure(null)
      void queryClient.invalidateQueries({ queryKey })
    },
    onError: (error) => setFailure(messageOf(error, t('app.error'))),
  })

  const download = useMutation({
    mutationFn: downloadAttachment,
    onSuccess: () => setFailure(null),
    onError: (error) => setFailure(messageOf(error, t('app.error'))),
  })

  if (query.isLoading) return <p>{t('app.loading')}</p>
  if (query.error) return <p role="alert">{messageOf(query.error, t('app.error'))}</p>

  const attachments = query.data ?? []
  return (
    <div className="card-gap">
      {failure ? <p className="notice notice-error" role="alert">{failure}</p> : null}

      {can(user, resource, 'update') ? (
        <label>
          {t('card.attachments.upload')}
          <input
            type="file"
            disabled={upload.isPending}
            onChange={(event) => {
              const file = event.target.files?.[0]
              if (file) upload.mutate(file)
              event.target.value = ''
            }}
          />
        </label>
      ) : null}

      {attachments.length === 0 ? (
        <p className="muted">{t('card.attachments.empty')}</p>
      ) : (
        <table className="data-table">
          <thead>
            <tr>
              <th>{t('card.attachments.name')}</th>
              <th>{t('card.attachments.size')}</th>
              <th>{t('card.attachments.status')}</th>
              <th>{t('card.attachments.action')}</th>
            </tr>
          </thead>
          <tbody>
            {attachments.map((attachment) => (
              <AttachmentRow
                key={attachment.id}
                attachment={attachment}
                downloading={download.isPending}
                onDownload={() => download.mutate(attachment)}
              />
            ))}
          </tbody>
        </table>
      )}
    </div>
  )
}

function AttachmentRow({
  attachment,
  downloading,
  onDownload,
}: {
  attachment: AttachmentView
  downloading: boolean
  onDownload: () => void
}) {
  const { t } = useApp()
  const status = t(`card.attachments.status.${attachment.scanStatus}`)
  return (
    <tr>
      <td>{attachment.fileName}</td>
      <td>{formatSize(attachment.sizeBytes)}</td>
      <td title={attachment.scanMessage ?? undefined}>{status}</td>
      <td>
        {attachment.downloadable ? (
          <button type="button" disabled={downloading} onClick={onDownload}>
            {t('card.attachments.download')}
          </button>
        ) : (
          <span className="muted">{t('card.attachments.unavailable')}</span>
        )}
      </td>
    </tr>
  )
}

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

function messageOf(error: unknown, fallback: string): string {
  return error instanceof ApiError ? error.message : fallback
}
